import { useEffect } from "react";
import {
  Button,
  HeroSection,
  InstructorSection,
  Seo,
  GridCards,
  FAQAccordion,
  Card,
} from "../../components";
import { Medal, Flame, Users, Swords, Trophy, HandCoins } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import "./HomePage.scss";

function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/o-nas") {
      const timeout = setTimeout(() => {
        document
          .getElementById("o-nas")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [location.pathname]);

  const serviceCards = [
    {
      title: "Workshopy",
      features: [
        "Jednorazové alebo krátkodobé umelecké workshopy",
        "Pre jednotlivcov, skupiny aj verejnosť",
        "Prístupná forma capoeiry bez nutnosti pravidelnej dochádzky",
      ],
      action: (
        <Button variant="ghost" onClick={() => navigate("/kontakt")}>
          Mám záujem
        </Button>
      ),
    },
    {
      title: "Team buildingy",
      features: [
        "Dynamické a originálne firemné akcie",
        "Spojenie pohybu, hudby a tímovej spolupráce",
        "Skvelá alternatíva ku klasickým teambuildingom",
      ],
      action: (
        <Button variant="ghost" onClick={() => navigate("/kontakt")}>
          Rezervovať termín
        </Button>
      ),
    },
    {
      title: "Show, prezentácie a vystúpenia",
      features: [
        "Atraktívne živé vystúpenia pre verejnosť",
        "Firemné akcie, festivaly a kultúrne podujatia",
        "Energia, akrobacia a krása capoeiry priamo u vás",
      ],
      action: (
        <Button variant="ghost" onClick={() => navigate("/kontakt")}>
          Naplánovať vystúpenie
        </Button>
      ),
    },
  ];

  return (
    <div className="home">
      <Seo
        title="Capoeira Livre – Professor Fogo – Brazílske bojové umenie"
        description="CAPOEIRA – Professor Fogo. Tréningy v Bratislave pre deti, mládež a dospelých. Prvá hodina zdarma, workshopy, vystúpenia a komunitný klub Academia Universum."
        canonical="/"
      />
      <HeroSection
        titles={["Vyskúšaj Capoeiru", "Prvý tréning zdarma"]}
        subtitle="S professorom Fogo trénerom majstrov sveta."
        actions={
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate("/cennik")}
          >
            <span className="btn__stacked">
              <span className="btn__main-text">Vyskúšať tréning</span>
              {/* <span className="btn__subtext">Stačia ti tepláky a tričko</span> */}
            </span>
          </Button>
        }
        // stats={[
        //   { value: "20+", label: "Rokov praxe" },
        //   { value: "Bratislava", label: "Novohradská 3" },
        //   { value: "ZDARMA", label: "Prvý tréning" },
        // ]}
        backgroundVideo="/CapoeiraVideo.mp4"
        backgroundImage="/pictures/hero_image.jpg"
        sideImage="/pictures/0-02-05-12d6a15bab85740f4e2e04629e009f8ea78ea8a1f04f91ad1cfd868f78eff2b1_fe62e7959a1f2fcf.PNG"
        sideImageAlt="Capoeira Livre"
      />

      <section className="home__section home__section--light" id="o-nas">
        <div className="home__container">
          <div className="home__section-header">
            <span className="home__eyebrow">O CAPOEIRE</span>
            <h2>Čo ti tréningy Capoeiry prinesú?</h2>
            <p>
              Kompletný pohybový tréning, ktorý ti dá silu, flexibilitu a mentálny reset po práci či škole. Vypni hlavu, spoznaj skvelú komunitu a posuň svoje hranice bez ohľadu na vek či kondíciu.
            </p>
          </div>

          <div className="home__pillars">
            <Card>
              <Card.Title as="h3">1. Boj</Card.Title>
              <Card.Subtitle>Sebaobrana a reakcia</Card.Subtitle>
              <Card.Body>
                <p>
                  Naučíš sa rýchlo reagovať, čítať pohyb partnera a získaš reálnu fyzickú aj psychickú odolnosť.                </p>
              </Card.Body>
            </Card>

            <Card>
              <Card.Title as="h3">2. Akrobacia</Card.Title>
              <Card.Subtitle>Pohybová sloboda</Card.Subtitle>
              <Card.Body>
                <p>
                  Od základných stojok až po dynamické prvky. Spevníš stred tela (core), zlepšíš koordináciu a získaš kontrolu nad vlastným telom.                </p>
              </Card.Body>
            </Card>

            <Card>
              <Card.Title as="h3">3. Komunita a rytmus</Card.Title>
              <Card.Subtitle>Energia & Hudba</Card.Subtitle>
              <Card.Body>
                <p>
                  Tradičná hudba, spev a kruh (roda) tvoria neopakovateľnú energiu. Nejde len o cvičenie, ale o komunitu ľudí, kde sa budeš cítiť vítaný.                </p>
              </Card.Body>
            </Card>
          </div>

          <div className="home__pillars-cta">
            <Button
              variant="primary"
              size="lg"
              className="home__pillars-btn"
              onClick={() => navigate("/cennik")}
            >
              <HandCoins className="home__pillars-btn-icon" /> Registrácia na prvú hodinu zadarmo
            </Button>
            <p className="home__pillars-microcopy">Stačia ti tepláky a tričko</p>
          </div>
        </div>
      </section>

      <InstructorSection
        tag="Nositeľ Radu Rio Branco"
        tagIcon={<Medal />}
        title1="Martin Fogo Halasz"
        description={`Fogo sa venuje rozvoju športu, kultúry a vzdelania prostredníctvom bojových umení pod hlavičkou Academia Universum. Je autorom konceptu troch pilierov: Telo, Myseľ, Spoločnosť.`}
        benefits={[
          {
            icon: <Medal />,
            text: "Štátne a športové vyznamenania",
            description: [
              "Ordem de Rio Branco (2019), hodnosť Oficial.",
              "Športovec roka - Tréner (Bratislava-Ružinov, 2019).",
              "Športovec roka - Osobnosť (Bratislava-Ružinov, 2020).",
            ],
          },
          {
            icon: <Swords />, // Ikona pre bojové umenia (nezabudni si ju importovať)
            text: "Cesta bojovými umeniami",
            description: [
              "Karate od roku 1996.",
              "Aikido od roku 1998, aktuálne držiteľ 3. Dan.",
              "Capoeira od roku 2001 v línii ABADÁ Capoeira.",
            ],
          },
          {
            icon: <Trophy />, // Ikona pre úspechy/výsledky (nezabudni si ju importovať)
            text: "Reprezentácia a výsledky",
            description: [
              "Svetový šampionát: 2007, 2011, 2013 ako reprezentant; 2015, 2017, 2019 ako hlavný tréner.",
              "1. miesto: V4, Otvorené majstrovstvá Rakúska (2x), Lokálne majstrovstvá ČR.",
              "2. miesto: Majstrovstvá východnej Európy. 3. miesto: Majstrovstvá Európy.",
            ],
          },
        ]}
        description2="Účasť na podujatiach s capoeirou: Angola, Azerbajdžan, Belgicko, Brazília, Česká republika, Francúzsko, Grécko, Izrael, Japonsko, Maďarsko, Nemecko, Poľsko, Portugalsko, Rakúsko, Rusko, Španielsko, Spojené arabské emiráty, Švédsko, Taliansko, Turecko a ďalšie krajiny."
        image="/pictures/ing_fogo.png"
        imageAlt="Inštruktor Capoeiry"
        citation={['"Capoeira é de todo mundo"', "– Capoeira je pre každého"]}
      />

      <GridCards
        tag="ĎALŠIE FORMY SPOLUPRÁCE"
        title="Workshopy, team buildingy a vystúpenia"
        subtitle="Capoeiru vieme priniesť aj mimo pravidelných tréningov."
        cards={serviceCards}
      />

      {/* <section className="home__section" id="o-capoeire">
        <div className="home__container home__container--narrow">
          <div className="home__section-header home__section-header--left">
            <span className="home__eyebrow">O CAPOEIRE</span>
            <h2>Umenie ukryté v kamufláži</h2>
          </div>

          <article className="home__article">
            <h3>Čo je capoeira?</h3>
            <p>
              Capoeira je umenie viacerých umení. Na prvý pohľad pôsobí ako
              čarovné divadlo, v ktorom splýva boj a tanec, akrobacia a rytmická
              hudba. Jej skutočná podstata je však ukrytá oveľa hlbšie. Capoeira
              je dokonalá kamufláž.
            </p>
            <p>
              Vznikla ako tichý vzdor afro-brazílskych otrokov proti nadvláde.
              Aby ukryli bojový systém, vplietli ho do rituálnych tancov a
              pohybov. Základom capoeiry je schopnosť predstierať, neustále sa
              meniť a byť v neustálom pohybe.
            </p>
            <blockquote>
              Capoeira je všetko, čo si daný okamih vyžaduje.
            </blockquote>

            <h3>Prečo dať na capoeiru svoje deti?</h3>
            <p>
              Kým iné krúžky rozvíjajú iba jednu oblasť, capoeira spája pohyb,
              rytmus, hudbu, spoluprácu a disciplínu v jednom celku. Viac ako 20
              rokov formujeme novú generáciu detí a dávame im zábavnú, dynamickú
              a zdravú alternatívu k svetu displejov.
            </p>
          </article>
        </div>
      </section> */}
    </div>
  );
}

export default HomePage;
